import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  LinearProgress,
  Checkbox,
  IconButton,
} from '@mui/joy';
import {
  DotsThreeVertical as MoreVertIcon,
  Paperclip as AttachFileIcon,
  ChatCircle as CommentIcon,
} from '@phosphor-icons/react';
import type { Task } from '../../types/tasks';

interface TaskCardProps {
  task: Task;
  onClick?: (task: Task) => void;
  onToggleComplete?: (taskId: number, completed: boolean) => void;
  onMenuClick?: (task: Task) => void;
  variant?: 'full' | 'compact';
  showMenu?: boolean;
}

const priorityColors = {
  low: 'neutral',
  medium: 'primary', 
  high: 'warning',
  critical: 'danger',
} as const;

const statusColors = {
  not_started: 'neutral',
  in_progress: 'primary',
  on_hold: 'warning',
  completed: 'success',
  cancelled: 'danger',
} as const;

export const TaskCard: React.FC<TaskCardProps> = ({
  task,
  onClick,
  onToggleComplete,
  onMenuClick,
  variant = 'full',
  showMenu = true,
}) => {
  const isCompleted = task.status === 'completed';
  
  const handleCheckboxChange = (checked: boolean) => {
    onToggleComplete?.(task.id, checked);
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger card click if checkbox or menu was clicked
    if (e.target instanceof HTMLInputElement || 
        (e.target as Element).closest('[role="button"]')) {
      return;
    }
    onClick?.(task);
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMenuClick?.(task);
  };

  return (
    <Card 
      variant="outlined"
      sx={{ 
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        '&:hover': onClick ? {
          boxShadow: 'md',
          borderColor: 'primary.300',
        } : {},
      }}
      onClick={handleCardClick}
    >
      <CardContent sx={{ p: variant === 'compact' ? 1 : 0.75 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Checkbox
            checked={isCompleted}
            onChange={(e) => handleCheckboxChange(e.target.checked)}
            sx={{ mt: 0.25 }}
          />
          
          <Box sx={{ flex: 1 }}>
            <Typography 
              level={variant === 'compact' ? "title-sm" : "title-md"}
              sx={{
                textDecoration: isCompleted ? 'line-through' : 'none',
                color: isCompleted ? 'text.tertiary' : 'text.primary',
                mb: variant === 'compact' ? 0.25 : 0.25,
              }}
            >
              {task.name}
            </Typography>
            
            {task.description && variant === 'full' && (
              <Typography level="body-sm" sx={{ mb: 0.25, color: 'text.secondary' }}>
                {task.description}
              </Typography>
            )}
            
            {task.due_date && variant === 'compact' && (
              <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 0.5 }}>
                Due: {new Date(task.due_date).toLocaleDateString()}
              </Typography>
            )}
            
            <Stack 
              direction="row" 
              spacing={1} 
              sx={{ mt: 0.25 }} 
              alignItems="center"
              flexWrap="wrap"
            >
              <Chip
                size="sm"
                variant="soft"
                color={priorityColors[task.priority]}
              >
                {task.priority_display}
              </Chip>
              
              {variant === 'full' && (
                <Chip
                  size="sm"
                  variant="soft"
                  color={statusColors[task.status]}
                >
                  {task.status_display}
                </Chip>
              )}
              
              {task.is_overdue && (
                <Chip size="sm" variant="solid" color="danger">
                  Overdue
                </Chip>
              )}
              
              {task.assigned_to_name && variant === 'full' && (
                <Chip size="sm" variant="plain">
                  {task.assigned_to_name}
                </Chip>
              )}
              
              {task.related_job_title && variant === 'full' && (
                <Chip size="sm" variant="plain" startDecorator="•">
                  Job: {task.related_job_title}
                </Chip>
              )}
              
              {task.related_opportunity_name && variant === 'full' && (
                <Chip size="sm" variant="plain" startDecorator="•">
                  Opp: {task.related_opportunity_name}
                </Chip>
              )}
            </Stack>
            
            {task.completion_percentage > 0 && task.completion_percentage < 100 && variant === 'full' && (
              <Box sx={{ mt: 0.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LinearProgress
                    determinate
                    value={task.completion_percentage}
                    sx={{ flex: 1 }}
                  />
                  <Typography level="body-xs">
                    {task.completion_percentage}%
                  </Typography>
                </Stack>
              </Box>
            )}
            
            {((task.attachments_count && task.attachments_count > 0) || 
              (task.comments_count && task.comments_count > 0) ||
              (task.attachments && task.attachments.length > 0) || 
              (task.comments && task.comments.length > 0)) && (
              <Stack direction="row" spacing={2} sx={{ mt: variant === 'compact' ? 0.5 : 0.5 }}>
                {((task.attachments_count && task.attachments_count > 0) || 
                  (task.attachments && task.attachments.length > 0)) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AttachFileIcon size={16} color="var(--joy-palette-text-secondary)" />
                    <Typography level="body-xs" color="neutral">
                      {task.attachments_count || task.attachments?.length || 0}
                    </Typography>
                  </Box>
                )}
                {((task.comments_count && task.comments_count > 0) || 
                  (task.comments && task.comments.length > 0)) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CommentIcon size={16} color="var(--joy-palette-primary-500)" />
                    <Typography level="body-xs" color="neutral">
                      {task.comments_count || task.comments?.length || 0}
                    </Typography>
                  </Box>
                )}
              </Stack>
            )}
          </Box>
          
          {showMenu && onMenuClick && (
            <IconButton 
              size="sm" 
              variant="plain"
              onClick={handleMenuClick}
              sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
            >
              <MoreVertIcon />
            </IconButton>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};